import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import {
  ImportHistory,
  ImportState,
  StreamPageEvent,
} from '../../../../../../core/interfaces/ui/external-import.interface';
import { LW_EXTERNAL_IMPORT_STORAGE_KEY } from '../../../../../../core/constants/external-import.constants';
import { ExternalImportService } from '../../../../../../core/services/ui/external-import.service';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../../../../core/utils/local-storage.utils';

@Component({
  selector: 'app-external-import',
  templateUrl: './external-import.component.html',
  styleUrl: './external-import.component.scss',
})
export class ExternalImportComponent implements OnInit, OnDestroy {
  state!: ImportState;

  private stateSub?: Subscription;

  constructor(
    private externalImportService: ExternalImportService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.stateSub = this.externalImportService.state$.subscribe((s) => {
      const wasDone = this.state?.isDone;
      this.state = s;
      if (!wasDone && s.isDone) {
        this.saveHistory(s.totalInserted, s.totalSkipped);
      }
    });
  }

  get isOpen(): boolean { return this.state.isOpen; }
  get isMinimized(): boolean { return this.state.isMinimized; }
  get isLoading(): boolean { return this.state.isLoading; }
  get isDone(): boolean { return this.state.isDone; }
  get hasError(): boolean { return this.state.hasError; }
  get isSetup(): boolean { return this.state.isOpen && !this.state.isLoading && !this.state.isDone && !this.state.hasError; }
  get errorMessage(): string { return this.state.errorMessage; }
  get pageEvents(): StreamPageEvent[] { return this.state.pageEvents; }
  get currentPage(): number { return this.state.currentPage; }
  get totalPages(): number { return this.state.totalPages; }
  get totalInserted(): number { return this.state.totalInserted; }
  get totalSkipped(): number { return this.state.totalSkipped; }
  get fromDate(): string { return this.state.fromDate; }
  get toDate(): string { return this.state.toDate; }
  get progressPercentage(): number { return this.state.progressPercentage; }

  get history(): ImportHistory | null {
    const raw = getLocalStorageItem(LW_EXTERNAL_IMPORT_STORAGE_KEY);
    try {
      const parsed = JSON.parse(raw);
      return parsed && parsed.date ? (parsed as ImportHistory) : null;
    } catch {
      return null;
    }
  }

  private get isDarkMode(): boolean {
    return document.body.classList.contains('dark-only');
  }

  private get swalTheme() {
    return this.isDarkMode
      ? { background: 'hsl(225, 14%, 17%)', color: 'rgba(255,255,255,0.6)' }
      : {};
  }

  async onImportClick(): Promise<void> {
    if (this.state.isLoading) {
      this.externalImportService.open();
      return;
    }

    const history = this.history;
    let confirmed = false;

    if (history) {
      const formattedDate = new Date(history.date).toLocaleString('es-ES');
      const result = await Swal.fire({
        icon: 'info',
        title: this.translate.instant('ChargesWithdrawals.Import.PreviousTitle'),
        html: this.translate.instant('ChargesWithdrawals.Import.PreviousBody', {
          date: formattedDate,
          inserted: history.totalInserted,
          skipped: history.totalSkipped,
        }),
        showCancelButton: true,
        confirmButtonText: this.translate.instant('ChargesWithdrawals.Import.PreviousConfirmBtn'),
        cancelButtonText: this.translate.instant('ChargesWithdrawals.Import.PreviousNo'),
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        ...this.swalTheme,
      });
      confirmed = result.isConfirmed;
    } else {
      const result = await Swal.fire({
        icon: 'question',
        title: this.translate.instant('ChargesWithdrawals.Import.ConfirmTitle'),
        html: this.translate.instant('ChargesWithdrawals.Import.ConfirmBody'),
        showCancelButton: true,
        confirmButtonText: this.translate.instant('ChargesWithdrawals.Import.ConfirmBtn'),
        cancelButtonText: this.translate.instant('common.cancel'),
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        ...this.swalTheme,
      });
      confirmed = result.isConfirmed;
    }

    if (!confirmed) return;
    this.externalImportService.openSetup();
  }

  startImport(): void {
    this.externalImportService.start();
  }

  onFromDateChange(event: Event): void {
    this.externalImportService.setFromDate((event.target as HTMLInputElement).value);
  }

  onToDateChange(event: Event): void {
    this.externalImportService.setToDate((event.target as HTMLInputElement).value);
  }

  private saveHistory(totalInserted: number, totalSkipped: number): void {
    const history: ImportHistory = {
      date: new Date().toISOString(),
      totalInserted,
      totalSkipped,
    };
    setLocalStorageItem(LW_EXTERNAL_IMPORT_STORAGE_KEY, JSON.stringify(history));
  }

  minimize(): void {
    this.externalImportService.minimize();
  }

  restore(): void {
    this.externalImportService.open();
  }

  close(): void {
    this.externalImportService.close();
  }

  formatDateRange(event: StreamPageEvent): string {
    if (!event.dateRange) return '';
    const fmt = (days: string) => {
      const [year, month, day] = days.split('-');
      return `${day}/${month}/${year.slice(2)}`;
    };
    const from = fmt(event.dateRange.from);
    const to = fmt(event.dateRange.to);
    return from === to ? from : `${from} → ${to}`;
  }

  trackByPage(_: number, event: StreamPageEvent): number {
    return event.page;
  }

  ngOnDestroy(): void {
    this.stateSub?.unsubscribe();
  }
}
